import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { AppSettings } from '../../app.settings';
import { UserCommonService } from '../../services/user-common.service';
import { MiFilterConfig } from './mi-filter.model';

@Component({
  selector: 'app-mi-filter',
  templateUrl: './mi-filter.component.html',
  styleUrls: ['./mi-filter.component.scss']
})
export class MiFilterComponent implements OnInit, OnChanges {

  @Input() config: MiFilterConfig[] = [];
  @Input() visible = false;
  @Input() title = 'label_filters';
  @Input() position = 'right';
  @Input() width = '400px';
  @Input() screenCode?: string;
  @Input() savedSelections: Record<string, any> | null = null;
  @Input() appendTo: any = 'body';
  @Input() contained = false;
  @Input() dismissible = true;
  /** false = the panel's Clear All only resets the draft selections; nothing is applied until Apply (LOC-US-005 FR-15). */
  @Input() clearAllAppliesImmediately = true;
  /** Optional fixed width (e.g. '202px') of the left category column; default is 35% of the panel. */
  @Input() categoryColumnWidth?: string;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() apply = new EventEmitter<Record<string, any>>();
  @Output() clearAll = new EventEmitter<Record<string, any>>();
  @Output() ready = new EventEmitter<Record<string, any>>();
  @Output() closed = new EventEmitter<void>();

  activeFilterCategory = '';
  filterSearchText = '';
  tempSelections: Record<string, any> = {};
  appliedSelections: Record<string, any> = {};

  private defaultSelections: Record<string, any> = {};
  private readyEmitted = false;

  constructor(private userCommonService: UserCommonService) {}

  ngOnInit(): void {
    if (this.config.length > 0) {
      this.activeFilterCategory = this.config[0].key;
    }
    this.initializeSelections();
    this.emitReadyIfNeeded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['config'] && !changes['config'].firstChange) || changes['savedSelections']) {
      this.initializeSelections();
      this.emitReadyIfNeeded();
    }

    if (changes['visible']?.currentValue === true) {
      this.onSidebarOpen();
    }
  }

  get activeConfig(): MiFilterConfig | undefined {
    return this.config.find(cfg => cfg.key === this.activeFilterCategory);
  }

  get sidebarAppendTo(): any {
    if (!this.isContained) {
      return 'body';
    }

    return this.appendTo?.nativeElement ?? this.appendTo ?? 'body';
  }

  get isContained(): boolean {
    return this.contained || (this.appendTo && this.appendTo !== 'body');
  }

  get sidebarStyle(): Record<string, string> {
    const style: Record<string, string> = { width: this.width };

    if (this.isContained) {
      style.position = 'absolute';
      style.top = '0';
      style.bottom = '0';
      style.height = 'auto';

      if (this.position === 'left') {
        style.left = '0';
      } else {
        style.right = '0';
      }
    }

    return style;
  }

  getTotalAppliedFilterCount(): number {
    let count = 0;
    this.config.forEach(cfg => {
      const value = this.appliedSelections[cfg.key];
      if (cfg.type === AppSettings.MI_FILTER_TYPE.MULTI_SELECT) {
        if (Array.isArray(value) && value.length > 0) {
          count++;
        }
      } else if (cfg.type === AppSettings.MI_FILTER_TYPE.DATE) {
        if (this.hasDateValue(value, cfg.dateRange)) {
          count++;
        }
      } else if (cfg.type === AppSettings.MI_FILTER_TYPE.DATE_RANGE) {
        if (this.hasDateValue(value, true)) {
          count++;
        }
      } else if (value !== null && value !== undefined && value !== '') {
        count++;
      }
    });
    return count;
  }

  onFilterCategoryClick(categoryKey: string): void {
    this.activeFilterCategory = categoryKey;
    this.filterSearchText = '';
  }

  getFilteredOptions(categoryKey: string): any[] {
    const cfg = this.config.find(item => item.key === categoryKey);
    if (!cfg?.options) {
      return [];
    }
    if (!this.filterSearchText) {
      return cfg.options;
    }
    const searchLower = this.filterSearchText.toLowerCase();
    return cfg.options.filter(option => option.label?.toLowerCase().includes(searchLower));
  }

  getTempSelections(categoryKey: string): any {
    return this.tempSelections[categoryKey];
  }

  isFilterOptionSelected(categoryKey: string, value: any): boolean {
    const cfg = this.config.find(item => item.key === categoryKey);
    if (!cfg) {
      return false;
    }
    const selections = this.getTempSelections(categoryKey);
    if (cfg.type === AppSettings.MI_FILTER_TYPE.SINGLE_SELECT) {
      return selections === value;
    }
    return Array.isArray(selections) && selections.includes(value);
  }

  isSingleSelectCategory(categoryKey: string): boolean {
    const cfg = this.config.find(item => item.key === categoryKey);
    return cfg?.type === AppSettings.MI_FILTER_TYPE.SINGLE_SELECT;
  }

  isMultiSelectCategory(categoryKey: string): boolean {
    const cfg = this.config.find(item => item.key === categoryKey);
    return cfg?.type === AppSettings.MI_FILTER_TYPE.MULTI_SELECT;
  }

  isDateCategory(categoryKey: string): boolean {
    const cfg = this.config.find(item => item.key === categoryKey);
    return cfg?.type === AppSettings.MI_FILTER_TYPE.DATE
      || cfg?.type === AppSettings.MI_FILTER_TYPE.DATE_RANGE;
  }

  isDateRangeMode(cfg: MiFilterConfig | undefined): boolean {
    return cfg?.type === AppSettings.MI_FILTER_TYPE.DATE_RANGE || cfg?.dateRange === true;
  }

  isCategoryDisabled(categoryKey: string): boolean {
    return !!this.config.find(item => item.key === categoryKey)?.disabled;
  }

  toggleFilterOption(categoryKey: string, value: any): void {
    const cfg = this.config.find(item => item.key === categoryKey);
    if (!cfg || cfg.disabled) {
      return;
    }

    if (cfg.type === AppSettings.MI_FILTER_TYPE.SINGLE_SELECT) {
      const current = this.getTempSelections(categoryKey);
      this.tempSelections[categoryKey] = current === value ? '' : value;
      return;
    }

    const selections = Array.isArray(this.getTempSelections(categoryKey))
      ? [...this.getTempSelections(categoryKey)]
      : [];
    const index = selections.indexOf(value);
    if (index >= 0) {
      selections.splice(index, 1);
    } else {
      selections.push(value);
    }
    this.tempSelections[categoryKey] = selections;
  }

  getSelectedCount(categoryKey: string): number {
    const cfg = this.config.find(item => item.key === categoryKey);
    if (!cfg) {
      return 0;
    }
    const selections = this.getTempSelections(categoryKey);
    if (cfg.type === AppSettings.MI_FILTER_TYPE.SINGLE_SELECT) {
      return selections ? 1 : 0;
    }
    if (cfg.type === AppSettings.MI_FILTER_TYPE.DATE) {
      return this.hasDateValue(selections, cfg.dateRange) ? 1 : 0;
    }
    if (cfg.type === AppSettings.MI_FILTER_TYPE.DATE_RANGE) {
      return this.hasDateValue(selections, true) ? 1 : 0;
    }
    return Array.isArray(selections) ? selections.length : 0;
  }

  selectAllOptions(categoryKey: string): void {
    const cfg = this.config.find(item => item.key === categoryKey);
    if (!cfg || cfg.type !== AppSettings.MI_FILTER_TYPE.MULTI_SELECT) {
      return;
    }
    const allValues = this.getFilteredOptions(categoryKey).map(option => option.value);
    const selections = Array.isArray(this.getTempSelections(categoryKey))
      ? [...this.getTempSelections(categoryKey)]
      : [];
    allValues.forEach(value => {
      if (!selections.includes(value)) {
        selections.push(value);
      }
    });
    this.tempSelections[categoryKey] = selections;
  }

  areAllFilteredOptionsSelected(categoryKey: string): boolean {
    const options = this.getFilteredOptions(categoryKey);
    const selections = this.getTempSelections(categoryKey);
    return options.length > 0 && Array.isArray(selections) && options.every(option => selections.includes(option.value));
  }

  /** Select All checkbox: ticks every listed (search-filtered) option, or clears them when they are all ticked already. */
  toggleSelectAllOptions(categoryKey: string): void {
    const cfg = this.config.find(item => item.key === categoryKey);
    if (!cfg || cfg.disabled || cfg.type !== AppSettings.MI_FILTER_TYPE.MULTI_SELECT) {
      return;
    }
    if (!this.areAllFilteredOptionsSelected(categoryKey)) {
      this.selectAllOptions(categoryKey);
      return;
    }
    const listed = this.getFilteredOptions(categoryKey).map(option => option.value);
    const selections = Array.isArray(this.getTempSelections(categoryKey)) ? this.getTempSelections(categoryKey) : [];
    this.tempSelections[categoryKey] = selections.filter(value => !listed.includes(value));
  }

  clearCategoryOptions(categoryKey: string): void {
    const cfg = this.config.find(item => item.key === categoryKey);
    if (!cfg) {
      return;
    }
    this.tempSelections[categoryKey] = this.getDefaultValue(cfg);
  }

  onFilterApply(): void {
    this.appliedSelections = this.cloneSelections(this.tempSelections);
    const payload = this.buildEmitPayload(this.appliedSelections);
    this.apply.emit(payload);
    if (this.screenCode) {
      this.persist();
    }
    this.closeSidebar();
  }

  onFilterClearAll(): void {
    if (!this.clearAllAppliesImmediately) {
      this.tempSelections = this.cloneSelections(this.defaultSelections);
      this.enforceDisabledSelections(this.tempSelections);
      return;
    }
    this.appliedSelections = this.cloneSelections(this.defaultSelections);
    this.tempSelections = this.cloneSelections(this.defaultSelections);
    this.enforceDisabledSelections(this.tempSelections);
    this.enforceDisabledSelections(this.appliedSelections);
    const payload = this.buildEmitPayload(this.appliedSelections);
    this.clearAll.emit(payload);
    if (this.screenCode) {
      this.persist();
    }
    this.closeSidebar();
  }

  onDateChange(categoryKey: string, value: any): void {
    this.tempSelections[categoryKey] = value;
  }

  isOptionsPresent(categoryKey: string): boolean {
    return this.getFilteredOptions(categoryKey).length > 0;
  }

  isSearchable(categoryKey: string): boolean {
    const cfg = this.config.find(item => item.key === categoryKey);
    return cfg?.searchable !== false;
  }

  closeSidebar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onSidebarHidden(): void {
    this.closed.emit();
  }

  onContainedMaskClick(): void {
    if (this.dismissible) {
      this.closeSidebar();
    }
  }

  resetToDefaults(): void {
    this.appliedSelections = this.cloneSelections(this.defaultSelections);
    this.tempSelections = this.cloneSelections(this.defaultSelections);
  }

  private onSidebarOpen(): void {
    this.tempSelections = this.cloneSelections(this.appliedSelections);
    this.enforceDisabledSelections(this.tempSelections);
    if (this.config.length > 0) {
      this.activeFilterCategory = this.config[0].key;
    }
    this.filterSearchText = '';
  }

  private initializeSelections(): void {
    this.defaultSelections = this.buildDefaultSelections(null);
    this.enforceDisabledSelections(this.defaultSelections);
    this.appliedSelections = this.buildDefaultSelections(this.savedSelections);
    this.enforceDisabledSelections(this.appliedSelections);
    this.tempSelections = this.cloneSelections(this.appliedSelections);
    if (this.config.length > 0 && !this.config.some(cfg => cfg.key === this.activeFilterCategory)) {
      this.activeFilterCategory = this.config[0].key;
    } else if (!this.activeFilterCategory && this.config.length > 0) {
      this.activeFilterCategory = this.config[0].key;
    }
  }

  private emitReadyIfNeeded(): void {
    if (!this.readyEmitted && this.config.length > 0) {
      this.ready.emit(this.buildEmitPayload(this.appliedSelections));
      this.readyEmitted = true;
    }
  }

  private buildDefaultSelections(savedSelections?: Record<string, any> | null): Record<string, any> {
    const selections: Record<string, any> = {};
    this.config.forEach(cfg => {
      if (cfg.type === AppSettings.MI_FILTER_TYPE.DATE_RANGE && cfg.fromKey && cfg.toKey) {
        const fromValue = savedSelections?.[cfg.fromKey];
        const toValue = savedSelections?.[cfg.toKey];
        const fromDate = fromValue !== undefined && fromValue !== null ? this.toDate(fromValue) : null;
        const toDate = toValue !== undefined && toValue !== null ? this.toDate(toValue) : null;
        selections[cfg.key] = fromDate || toDate ? [fromDate, toDate] : this.getDefaultValue(cfg);
        return;
      }
      const savedValue = savedSelections?.[cfg.key];
      selections[cfg.key] = savedValue !== undefined && savedValue !== null
        ? this.deserializeValue(cfg, savedValue)
        : this.getDefaultValue(cfg);
    });
    return selections;
  }

  private getDefaultValue(cfg: MiFilterConfig): any {
    if (cfg.defaultValue !== undefined) {
      if (cfg.type === AppSettings.MI_FILTER_TYPE.MULTI_SELECT && Array.isArray(cfg.defaultValue)) {
        return [...cfg.defaultValue];
      }
      return cfg.defaultValue;
    }
    if (cfg.type === AppSettings.MI_FILTER_TYPE.MULTI_SELECT) {
      return [];
    }
    if (cfg.type === AppSettings.MI_FILTER_TYPE.DATE || cfg.type === AppSettings.MI_FILTER_TYPE.DATE_RANGE) {
      return null;
    }
    return '';
  }

  private deserializeValue(cfg: MiFilterConfig, value: any): any {
    if (cfg.type === AppSettings.MI_FILTER_TYPE.MULTI_SELECT) {
      if (Array.isArray(value)) {
        return [...value];
      }
      if (typeof value === 'string') {
        return value ? value.split(',').map(item => item.trim()).filter(Boolean) : [];
      }
      return [];
    }
    if (cfg.type === AppSettings.MI_FILTER_TYPE.DATE) {
      if (cfg.dateRange && Array.isArray(value)) {
        return value.map(item => this.toDate(item)).filter(Boolean);
      }
      return this.toDate(value);
    }
    return value;
  }

  private serializeSelections(selections: Record<string, any>): Record<string, any> {
    const serialized: Record<string, any> = {};
    this.config.forEach(cfg => {
      const value = selections[cfg.key];
      if (cfg.type === AppSettings.MI_FILTER_TYPE.MULTI_SELECT) {
        serialized[cfg.key] = Array.isArray(value) ? value.join(',') : '';
      } else if (cfg.type === AppSettings.MI_FILTER_TYPE.DATE) {
        if (cfg.dateRange && Array.isArray(value)) {
          serialized[cfg.key] = value.map(item => this.serializeDate(item));
        } else {
          serialized[cfg.key] = this.serializeDate(value);
        }
      } else if (cfg.type === AppSettings.MI_FILTER_TYPE.DATE_RANGE && cfg.fromKey && cfg.toKey) {
        const range = Array.isArray(value) ? value : [];
        serialized[cfg.fromKey] = this.serializeDate(range[0]);
        serialized[cfg.toKey] = this.serializeDate(range[1]);
      } else {
        serialized[cfg.key] = value ?? '';
      }
    });
    return serialized;
  }

  persist(): void {
    if (!this.screenCode) {
      return;
    }
    const eventId = this.userCommonService.getActiveEventId();
    if (!eventId) {
      return;
    }
    const model = {
      screenCode: this.screenCode,
      eventId,
      filterData: this.serializeSelections(this.appliedSelections)
    };
    this.userCommonService.updateSavedFilter(model).subscribe(result => {
      this.userCommonService.updateFilterDataToLocal(result);
    });
  }

  private cloneSelections(selections: Record<string, any>): Record<string, any> {
    const cloned: Record<string, any> = {};
    this.config.forEach(cfg => {
      const value = selections[cfg.key];
      if (cfg.type === AppSettings.MI_FILTER_TYPE.MULTI_SELECT) {
        cloned[cfg.key] = Array.isArray(value) ? [...value] : [];
      } else if (
        (cfg.type === AppSettings.MI_FILTER_TYPE.DATE && cfg.dateRange && Array.isArray(value))
        || (cfg.type === AppSettings.MI_FILTER_TYPE.DATE_RANGE && Array.isArray(value))
      ) {
        cloned[cfg.key] = [...value];
      } else {
        cloned[cfg.key] = value;
      }
    });
    return cloned;
  }

  private buildEmitPayload(selections: Record<string, any>): Record<string, any> {
    const payload = this.cloneSelections(selections);
    this.config.forEach(cfg => {
      if (cfg.type === AppSettings.MI_FILTER_TYPE.DATE_RANGE && cfg.fromKey && cfg.toKey) {
        const range = payload[cfg.key];
        if (Array.isArray(range)) {
          payload[cfg.fromKey] = range[0] ?? null;
          payload[cfg.toKey] = range[1] ?? null;
        } else {
          payload[cfg.fromKey] = null;
          payload[cfg.toKey] = null;
        }
        delete payload[cfg.key];
      }
    });
    return payload;
  }

  private hasDateValue(value: any, dateRange?: boolean): boolean {
    if (dateRange) {
      return Array.isArray(value) && value.length > 0 && value.some(item => !!item);
    }
    return !!value;
  }

  private toDate(value: any): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private serializeDate(value: any): string {
    if (!value) {
      return '';
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    return value.toString();
  }

  private enforceDisabledSelections(selections: Record<string, any>): void {
    this.config.forEach(cfg => {
      if (cfg.disabled) {
        selections[cfg.key] = cfg.defaultValue !== undefined
          ? cfg.defaultValue
          : this.getDefaultValue(cfg);
      }
    });
  }
}
